import React, { useState, useEffect } from 'react';
import { 
  Box, 
  Typography, 
  TextField, 
  Paper, 
  Alert,
  Button,
  LinearProgress,
  Chip
} from '@mui/material';
import { processUploadedChatLog } from '../../utils/chatLogProcessor';

const ChatLogInput = ({ formData, updateFormData, onValidationChange }) => {
  const [chatLog, setChatLog] = useState(formData.chatLog || '');
  const [uploadedFile, setUploadedFile] = useState(null);
  const [processing, setProcessing] = useState(false);
  const [error, setError] = useState('');
  const [wordCount, setWordCount] = useState(0);
  const [tokenCount, setTokenCount] = useState(0);
  const [compressionInfo, setCompressionInfo] = useState(null);
  const fileInputRef = React.useRef();

  useEffect(() => {
    // Count words in chat log
    const words = chatLog.trim().split(/\s+/).filter(w => w.length > 0);
    const count = words.length;
    setWordCount(count);
    
    // Estimate tokens (rough: 1 token ≈ 4 characters)
    const estimatedTokens = Math.ceil(chatLog.length / 4);
    setTokenCount(estimatedTokens);

    // Validate - now requires at least 1000 words, accepts up to 50,000
    const isValid = count >= 1000 && count <= 50000;
    onValidationChange(isValid);

    if (count > 0 && count < 1000) {
      setError(`Chat log too short: ${count} words (minimum 1,000 required for accurate analysis)`);
    } else if (count > 50000) {
      setError(`Chat log too long: ${count.toLocaleString()} words (maximum 50,000 after processing). File will be auto-sampled.`);
    } else {
      setError('');
    }

    // Update form data
    updateFormData({
      chatLog,
      chatLogWordCount: count,
      chatLogTokenCount: estimatedTokens,
      chatLogProcessedAt: new Date().toISOString(),
      chatLogCompressionRatio: compressionInfo?.compressionRatio || null,
      chatLogSamplingApplied: compressionInfo?.samplingApplied || false
    });
  }, [chatLog]);

  const handleFileUpload = async (event) => {
    const file = event.target.files[0];
    if (!file) return;

    setProcessing(true);
    setError('');
    setUploadedFile(file.name);

    try {
      console.log('Processing uploaded file:', file.name);
      const result = await processUploadedChatLog(file);

      console.log('Processing result:', result);

      if (!result.valid) {
        setError(result.error);
        setProcessing(false);
        return;
      }

      // Set the optimized chat log text
      setChatLog(result.optimizedText);
      
      // Store compression info
      setCompressionInfo({
        originalSize: result.originalSize,
        optimizedSize: result.optimizedSize,
        compressionRatio: result.compressionRatio,
        conversationCount: result.conversationCount,
        totalMessages: result.totalMessages,
        dateRange: result.dateRange,
        samplingApplied: result.samplingApplied,
        needsSampling: result.needsSampling,
        warning: result.warning
      });

      setProcessing(false);
      
    } catch (err) {
      console.error('Error processing file:', err);
      setError(err.message || 'Failed to process file. Please try again.');
      setProcessing(false);
    }

    // Reset file input
    event.target.value = '';
  };

  const handlePaste = (e) => {
    // Allow normal paste behavior
    // The text will be picked up by the onChange handler
  };

  const getWordCountColor = () => {
    if (wordCount < 1000) return '#ef4444'; // Red
    if (wordCount > 50000) return '#ef4444'; // Red
    if (wordCount < 3000) return '#f59e0b'; // Orange - good but could be better
    return '#10b981'; // Green - optimal range
  };

  return (
    <Box sx={{ maxWidth: 900, margin: '0 auto', padding: 3 }}>
      <Typography variant="h5" gutterBottom sx={{ 
        fontWeight: 300, 
        mb: 3,
        fontFamily: 'monospace',
        color: '#fafafa'
      }}>
        Step 2: Chat Log Submission (1,000+ words required)
      </Typography>
      
      <Typography variant="body1" sx={{ mb: 3, color: '#737373', lineHeight: 1.6 }}>
        We need examples of how you interact, ask questions, and argue points. The more authentic the conversation, the more accurate your cognitive profile.
      </Typography>

      {/* File Upload Option */}
      <Paper sx={{ 
        mb: 3, 
        p: 3, 
        backgroundColor: '#171717', 
        border: '1px solid #262626',
        borderRadius: '8px'
      }}>
        <Typography variant="subtitle1" gutterBottom sx={{ color: '#fafafa', fontFamily: 'monospace', mb: 2, fontWeight: 600 }}>
          Option 1: Upload ChatGPT Export
        </Typography>
        
        <Typography variant="body2" sx={{ mb: 2, color: '#737373', lineHeight: 1.6 }}>
          <strong style={{ color: '#fafafa' }}>How to get your ChatGPT logs:</strong>
        </Typography>
        
        <Box component="ol" sx={{ pl: 3, mb: 2, color: '#737373' }}>
          <li style={{ marginBottom: '8px' }}>In ChatGPT, click on your profile (bottom left)</li>
          <li style={{ marginBottom: '8px' }}>Select <strong style={{ color: '#fafafa' }}>Settings</strong> → <strong style={{ color: '#fafafa' }}>Data controls</strong></li>
          <li style={{ marginBottom: '8px' }}>Click <strong style={{ color: '#fafafa' }}>Export data</strong></li>
          <li style={{ marginBottom: '8px' }}>Click <strong style={{ color: '#fafafa' }}>Confirm export</strong></li>
          <li style={{ marginBottom: '8px' }}>Within 1-2 minutes, a download link will appear in your inbox</li>
          <li style={{ marginBottom: '8px' }}>Download the ZIP file and upload it here</li>
        </Box>
        
        <Alert severity="info" sx={{ mb: 2, backgroundColor: '#0a0a0a', border: '1px solid #3b82f6', color: '#fafafa' }}>
          <Typography variant="body2" sx={{ fontSize: '0.875rem' }}>
            <strong>🔒 Security:</strong> Your chat logs are only stored temporarily, then automatically deleted. We keep only the insights, never the raw conversations.
          </Typography>
        </Alert>
        
        <Typography variant="body2" sx={{ mb: 2, color: '#525252', fontSize: '0.85rem', fontStyle: 'italic' }}>
          Large files (168MB+) are welcome — we'll intelligently sample your most recent conversations for optimal analysis.
        </Typography>
        
        <input
          ref={fileInputRef}
          type="file"
          accept=".zip,.txt,.json"
          onChange={handleFileUpload}
          style={{ display: 'none' }}
        />
        
        <Button
          variant="contained"
          onClick={() => fileInputRef.current.click()}
          disabled={processing}
          sx={{
            backgroundColor: '#fafafa',
            color: '#0a0a0a',
            fontFamily: 'monospace',
            '&:hover': { backgroundColor: '#e5e5e5' },
            '&.Mui-disabled': { backgroundColor: '#262626', color: '#525252' }
          }}
        >
          {processing ? 'Processing...' : 'Upload File (.zip, .txt, .json)'}
        </Button>

        {uploadedFile && (
          <Chip 
            label={`Uploaded: ${uploadedFile}`} 
            sx={{ ml: 2, backgroundColor: '#262626', color: '#fafafa', fontFamily: 'monospace' }} 
          />
        )}

        {processing && <LinearProgress sx={{ mt: 2, backgroundColor: '#262626' }} />}
      </Paper>

      {/* Text Input Option */}
      <Paper sx={{ 
        mb: 3, 
        p: 3, 
        backgroundColor: '#171717', 
        border: '1px solid #262626',
        borderRadius: '8px'
      }}>
        <Typography variant="subtitle1" gutterBottom sx={{ color: '#fafafa', fontFamily: 'monospace', mb: 2, fontWeight: 600 }}>
          Option 2: Paste Text Directly
        </Typography>
        
        <Typography variant="body2" sx={{ mb: 2, color: '#737373', lineHeight: 1.6 }}>
          <strong style={{ color: '#fafafa' }}>What works best:</strong>
        </Typography>
        
        <Box component="ul" sx={{ pl: 3, mb: 2, color: '#737373' }}>
          <li style={{ marginBottom: '6px' }}>Casual conversations with depth (friends, partners, therapists)</li>
          <li style={{ marginBottom: '6px' }}>A convincing argument or debate where you defend a position</li>
          <li style={{ marginBottom: '6px' }}>Explaining a complex topic or teaching someone</li>
          <li style={{ marginBottom: '6px' }}>Chat logs from Claude, Grok, Gemini, or other AI assistants</li>
          <li style={{ marginBottom: '6px' }}>Written papers, essays, or long-form reflections</li>
          <li style={{ marginBottom: '6px' }}>Work discussions where you problem-solve or strategize</li>
        </Box>
        
        <Typography variant="body2" sx={{ mb: 2, color: '#525252', fontSize: '0.85rem', fontStyle: 'italic' }}>
          We need to see how you interact, ask questions, and argue points — not just what you know, but how you think.
        </Typography>
        
        <TextField
          fullWidth
          multiline
          rows={16}
          value={chatLog}
          onChange={(e) => setChatLog(e.target.value)}
          onPaste={handlePaste}
          placeholder="Paste your conversation here (minimum 1,000 words)...

Example format:
User: [Your message]
AI: [Response]
User: [Your follow-up]
..."
          variant="outlined"
          sx={{
            '& .MuiOutlinedInput-root': {
              backgroundColor: '#0a0a0a',
              color: '#fafafa',
              fontFamily: 'monospace',
              fontSize: '0.875rem',
              '& fieldset': { borderColor: '#262626' },
              '&:hover fieldset': { borderColor: '#404040' },
              '&.Mui-focused fieldset': { borderColor: '#525252' }
            }
          }}
        />

        {/* Word Count Indicator */}
        <Box sx={{ mt: 2, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <Typography 
            variant="body2" 
            sx={{ 
              color: getWordCountColor(), 
              fontFamily: 'monospace',
              fontWeight: 600 
            }}
          >
            {wordCount.toLocaleString()} words / ~{tokenCount.toLocaleString()} tokens
          </Typography>
          
          <Typography variant="body2" sx={{ color: '#525252', fontFamily: 'monospace' }}>
            {wordCount < 1000 && `Need ${(1000 - wordCount).toLocaleString()} more words`}
            {wordCount >= 1000 && wordCount < 3000 && '✓ Good (3,000+ words recommended)'}
            {wordCount >= 3000 && wordCount <= 50000 && '✓ Excellent length'}
            {wordCount > 50000 && `${(wordCount - 50000).toLocaleString()} words over limit (will auto-sample)`}
          </Typography>
        </Box>

        {/* Compression Info */}
        {compressionInfo && (
          <Box sx={{ mt: 2 }}>
            <Alert severity="success" sx={{ backgroundColor: '#171717', border: '1px solid #10b981', color: '#fafafa' }}>
              <Typography variant="body2" sx={{ fontFamily: 'monospace' }}>
                <strong>Processed:</strong> {compressionInfo.compressionRatio}% compression 
                ({compressionInfo.conversationCount} conversations, {compressionInfo.totalMessages} messages)
                {compressionInfo.dateRange && ` • ${compressionInfo.dateRange.start} to ${compressionInfo.dateRange.end}`}
              </Typography>
            </Alert>
            
            {/* Sampling Warning for Large Files */}
            {compressionInfo.needsSampling && (
              <Alert severity="info" sx={{ mt: 2, backgroundColor: '#171717', border: '1px solid #3b82f6', color: '#fafafa' }}>
                <Typography variant="body2" sx={{ fontFamily: 'monospace' }}>
                  <strong>📊 Large File Detected:</strong> {compressionInfo.warning}
                  <br />
                  We'll analyze your <strong>most recent conversations</strong> for the most accurate forecast.
                </Typography>
              </Alert>
            )}
          </Box>
        )}
      </Paper>

      {/* Error Display */}
      {error && (
        <Alert severity="error" sx={{ mb: 3, backgroundColor: '#171717', border: '1px solid #ef4444', color: '#fafafa' }}>
          {error}
        </Alert>
      )}

      {/* Privacy Notice */}
      <Paper sx={{ 
        p: 3, 
        backgroundColor: '#171717', 
        border: '1px solid #262626',
        borderRadius: '8px'
      }}>
        <Typography variant="subtitle2" sx={{ color: '#fafafa', mb: 2, fontWeight: 600 }}>
          🔒 Privacy & Security
        </Typography>
        <Typography variant="body2" sx={{ color: '#737373', mb: 1, lineHeight: 1.6 }}>
          • Chat logs are stored <strong style={{ color: '#fafafa' }}>temporarily for processing only</strong>
        </Typography>
        <Typography variant="body2" sx={{ color: '#737373', mb: 1, lineHeight: 1.6 }}>
          • Automatically deleted after analysis (or 30 days maximum)
        </Typography>
        <Typography variant="body2" sx={{ color: '#737373', mb: 1, lineHeight: 1.6 }}>
          • We keep only the analyzed insights, never the raw conversations
        </Typography>
        <Typography variant="body2" sx={{ color: '#737373', mb: 1, lineHeight: 1.6 }}>
          • One free regeneration included (in case you want to try again)
        </Typography>
        <Typography variant="body2" sx={{ color: '#737373', lineHeight: 1.6 }}>
          • You can delete your report anytime from your dashboard
        </Typography>
      </Paper>

      {/* Instructions */}
      <Paper sx={{ 
        mt: 3, 
        p: 2, 
        backgroundColor: '#0a0a0a', 
        border: '1px solid #262626',
        borderRadius: '8px'
      }}>
        <Typography variant="body2" sx={{ color: '#737373', fontStyle: 'italic', lineHeight: 1.6 }}>
          <strong style={{ color: '#fafafa' }}>💡 What makes a great submission:</strong> The more authentic and unrehearsed the conversation, 
          the more accurate your cognitive profile. We're looking for how you think, not what you know — so casual conversations with depth, 
          problem-solving discussions, or debates work better than formal writing.
        </Typography>
      </Paper>
    </Box>
  );
};

export default ChatLogInput;

